'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["mode", "message", "messageId", "index", "items", "slots", "slotProps", "children"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useStreamingIndicatorVisibility } from "../hooks/useStreamingIndicatorVisibility.js";
import { useMessageContext } from "../message/internals/MessageContext.js";
import { getDataAttributes } from "../internals/getDataAttributes.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Animated "response in flight" indicator. Outside a message it covers the
 * waiting phase (request sent, no assistant message yet); inside a message it
 * renders while that assistant message is streaming.
 */
export const StreamingIndicator = /*#__PURE__*/React.forwardRef(function StreamingIndicator(props, ref) {
  const {
      mode = 'auto',
      message: messageProp,
      messageId,
      index,
      items,
      slots,
      slotProps,
      children
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const messageContext = useMessageContext();
  const message = messageProp !== undefined ? messageProp : messageContext.message;
  const {
    waiting
  } = useStreamingIndicatorVisibility(mode);

  // In-message placement: the streaming assistant message itself is the proof
  // of an assistant-backed conversation, so `'auto'` needs no extra detection.
  let phase = null;
  if (message != null) {
    if (message.role === 'assistant' && message.status === 'streaming' && mode !== false) {
      phase = 'streaming';
    }
  } else if (waiting) {
    phase = 'waiting';
  }
  const ownerState = React.useMemo(() => ({
    phase,
    messageId: message?.id ?? messageId
  }), [message?.id, messageId, phase]);
  const Root = slots?.root ?? 'div';
  const rootProps = useSlotProps({
    elementType: Root,
    externalSlotProps: slotProps?.root,
    externalForwardedProps: other,
    ownerState,
    additionalProps: _extends({
      ref,
      // Decorative: streaming start/finish is already announced by the
      // message list's status live region, so the dots stay silent.
      'aria-hidden': true
    }, getDataAttributes({
      phase
    }))
  });

  // Trailing-row contract: only the last rendered row shows the indicator.
  if (items != null && index != null && index !== items.length - 1) {
    return null;
  }
  if (phase == null) {
    return null;
  }
  return /*#__PURE__*/_jsx(Root, _extends({}, rootProps, {
    children: children ?? /*#__PURE__*/_jsxs(React.Fragment, {
      children: [/*#__PURE__*/_jsx("span", {}), /*#__PURE__*/_jsx("span", {}), /*#__PURE__*/_jsx("span", {})]
    })
  }));
});
if (process.env.NODE_ENV !== "production") StreamingIndicator.displayName = "StreamingIndicator";